// Used by useQueryParamsActions only
import { useCallback } from "react";

export type QueryParamNavOptions = { replace?: boolean };

/**
 * Shape React Router writes into `window.history.state` on every navigation
 * (@remix-run/router `getHistoryState`). BrowserRouter tracks its position in
 * the session history stack via `idx`; each location gets a unique `key`.
 * `usr` is optional location.state passed to navigate().
 */
type RouterHistoryState = {
  usr: unknown;
  key: string;
  idx: number;
};

/** Same algorithm as @remix-run/router `createKey`. */
const createHistoryKey = () => Math.random().toString(36).slice(2, 10);

const isRouterHistoryState = (state: unknown): state is RouterHistoryState =>
  typeof state === "object" &&
  state !== null &&
  typeof (state as RouterHistoryState).idx === "number";

/** Read React Router's history metadata from the current entry, with safe fallbacks. */
const getRouterHistoryState = (): RouterHistoryState => {
  const state: unknown = window.history.state;
  if (isRouterHistoryState(state)) {
    return {
      usr: state.usr ?? null,
      key: typeof state.key === "string" ? state.key : "default",
      idx: state.idx,
    };
  }
  // Entry wasn't created by React Router (direct URL load, external pushState, etc.)
  return { usr: null, key: "default", idx: 0 };
};

/**
 * Build state for a new history entry when pushing (replace: false).
 *
 * Must NOT reuse `window.history.state` as-is: that copies the same `idx` and
 * `key` onto the new entry. On popstate, React Router computes
 * `delta = nextIdx - currentIdx`; duplicate idx values yield delta 0, so Back
 * appears to do nothing (e.g. assessment student queue switching via access-id).
 *
 * Mirrors @remix-run/router `push()`: preserve usr, new random key, idx + 1.
 */
const createPushHistoryState = (current: RouterHistoryState): RouterHistoryState => ({
  usr: current.usr,
  key: createHistoryKey(),
  idx: current.idx + 1,
});

/**
 * Updates the browser URL for query-param changes without React Router navigate().
 * That avoids BrowserRouter re-rendering the whole tree on overlay/sidebar toggles.
 * Redux query params (updated by the caller) remain the source of truth for the app;
 * popstate → useNavigationObserver keeps Redux in sync on back/forward.
 *
 * Tests: setupTests.tsx mocks this to call React Router navigate() so
 * MemoryRouter (see testUtils AllTheProviders) stays in sync — MemoryRouter
 * does not observe replaceState/pushState.
 */
export const useMockableNavigate = () => {
  return useCallback(
    (params: URLSearchParams, location: Location, options?: QueryParamNavOptions) => {
      const search = params.toString();
      const next = `${location.pathname}${search ? `?${search}` : ""}${location.hash || ""}`;
      const current = `${location.pathname}${location.search}${location.hash || ""}`;

      if (next === current) {
        return;
      }

      // Most query-param updates (overlays, sidebars) are ephemeral UI state — replace
      // the current entry so Back leaves the page instead of undoing each toggle.
      const replace = options?.replace ?? true;
      if (replace) {
        // Same history entry: keep idx/key so React Router's stack position is unchanged.
        window.history.replaceState(window.history.state, "", next);
      } else {
        // Intentional navigation (e.g. picking another student): push a new entry so
        // Back restores the previous query string. Requires fresh idx/key (see above).
        window.history.pushState(createPushHistoryState(getRouterHistoryState()), "", next);
      }
    },
    [],
  );
};
